<template>
  <v-menu
    offset-y
    left
    nudge-bottom="14"
    max-width="300"
    :close-on-content-click="false"
    content-class="user-profile-menu-content"
  >
    <template v-slot:activator="{ on, attrs }">
      <span>
        <v-avatar
          v-if="user.firstName && user.lastName"
          data-test-user-menu
          size="35px"
          :color="color"
          class="white--text ml-4"
          :right="true"
          v-bind="attrs"
          v-on="on"
        >
          {{ user.firstName[0] || "" }}{{ user.lastName[0] || "" }}
        </v-avatar>
      </span>
    </template>
    <v-list>
      <v-list-item>
        <v-list-item-avatar>
          <v-avatar :color="color" class="white--text" size="35px" v-if="user.firstName && user.lastName">
            {{ user.firstName[0] || "" }}{{ user.lastName[0] || "" }}
          </v-avatar>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title> {{ user.firstName }} {{ user.lastName }}</v-list-item-title>
          <v-list-item-subtitle class="text--disabled">{{ user.userName }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-divider
        class="my-2 pb-1"
        v-if="partners.length > 0 && $can(ActionEnum.READ, ResourceEnum.PARTNER)"
      ></v-divider>

      <v-list-item v-if="partners.length > 0">
        <the-partner-selector-list
          v-if="$can(ActionEnum.READ, ResourceEnum.PARTNER) && partners.length > 1"
        ></the-partner-selector-list>
        <v-list-item-content v-else>
          <v-list-item-title>{{ partners[0].companyName }}</v-list-item-title>
          <v-list-item-subtitle class="text--disabled">{{ partners[0].id }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <v-list-item v-if="isReport && permissionFleetIds.length">
        <v-list-item-content v-if="permissionFleets.length === 1">
          <v-list-item-title>{{ firstParentFleet.title }}</v-list-item-title>
          <v-list-item-subtitle class="text--disabled">
            {{ firstParentFleet.id }}
          </v-list-item-subtitle>
        </v-list-item-content>
        <the-parent-fleet-selector-list v-else />
      </v-list-item>

      <v-divider class="my-2"></v-divider>

      <!-- Profile -->
      <v-list-item link :to="userItem.link" data-test-profile-link>
        <v-list-item-icon class="me-2">
          <v-icon size="22">
            {{ userItem.icon }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ userItem.text }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="my-2"></v-divider>

      <!-- Logout -->
      <v-list-item @click="performLogout">
        <v-list-item-icon class="me-2" v-if="!isLogoutLoading">
          <v-icon size="22">
            {{ logOut.icon }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-icon class="me-2" v-else>
          <v-progress-circular :size="22" :width="3" color="grey" indeterminate></v-progress-circular>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ logOut.text }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import TheParentFleetSelectorList from "@/layouts/navigation/TheParentFleetSelector.vue";
import ThePartnerSelectorList from "@/layouts/navigation/ThePartnerSelectorList.vue";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { IUser } from "@/models/user.entity";
import { MrfiktivFleetViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { ConfigModule } from "@/store/modules/config";
import { FleetManagerModule } from "@/store/modules/fleet/manager-fleet.store";
import { UserModule } from "@/store/modules/me-user.store";
import { PartnerModule } from "@/store/modules/partner";
import { Component, Prop, Vue } from "vue-property-decorator";

@Component({
  components: { ThePartnerSelectorList, TheParentFleetSelectorList }
})
export default class ProfileMenu extends Vue {
  @Prop({ default: false })
  isEndUser!: boolean;

  ActionEnum = ActionEnum;
  ResourceEnum = ResourceEnum;

  logout() {
    this.$router.push({ name: "Logout" });
  }

  get partner() {
    return PartnerModule.partner;
  }

  get partners() {
    return PartnerModule.partners;
  }

  get companyName() {
    return PartnerModule.partner?.companyName || "";
  }

  get isReport() {
    return AppContextEnum.REPORT === ConfigModule.appContext;
  }

  get fleetPartnerPermission() {
    if (this.partner.id) {
      return UserModule.fleetPermission.filter((p) => this.partner.id === p.partnerId);
    }

    return UserModule.fleetPermission;
  }

  /**
   * Distinct list of fleetId found in fleet permission for current partner.
   */
  get permissionFleetIds() {
    return [...new Set(this.fleetPartnerPermission.map((p) => p.fleetId))];
  }

  get permissionFleets(): MrfiktivFleetViewModelGen[] {
    return FleetManagerModule.permissionFleets.filter((f) => f.partnerId === this.partner.id);
  }

  get parentFleets(): MrfiktivFleetViewModelGen[] {
    return FleetManagerModule.fleets;
  }

  get firstParentFleet() {
    return this.parentFleets?.[0];
  }

  /**
   * Returns primary color if !enduser, else returns custom partner color
   */
  get color() {
    if (this.isEndUser) {
      return getDefaultPartnerColor();
    } else {
      return "primary";
    }
  }

  get isAuthenticated() {
    return UserModule.isAuthenticated;
  }

  get user(): IUser {
    return UserModule.user;
  }

  get logOut() {
    const item = {
      icon: "mdi-logout",
      text: "Logout",
      link: "/login/logout"
    };
    return item;
  }

  isLogoutLoading = false;

  async performLogout() {
    this.isLogoutLoading = true;
    try {
      await UserModule.logout();
    } catch {
      this.isLogoutLoading = false;
    }
  }

  get userItem() {
    const item = {
      icon: "mdi-account-outline",
      text: "Profil",
      link: "/profile"
    };
    return item;
  }
}
</script>

<style lang="scss">
.user-profile-menu-content {
  .v-list-item {
    min-height: 2.5rem !important;
  }
}
</style>
